/**
 * Award History Chart Component
 *
 * Compact Recharts line chart showing award history as % of max eligible award
 * per quarter. Tooltips display actual dollar amounts. Hidden when no data.
 *
 * Phase 7: Leadership Inline Expansion (LEAD-04, LEAD-05, LEAD-06)
 */

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { AwardHistoryItem } from '@/lib/equip-types'

interface AwardHistoryChartProps {
  history: AwardHistoryItem[]
  className?: string
}

interface ChartDataPoint {
  label: string
  percentOfMax: number
  award: number | null
  maxAward: number | null
}

// Custom tooltip showing dollar amounts and percentage
function AwardTooltip({ active, payload, label }: {
  active?: boolean
  payload?: Array<{ payload: ChartDataPoint }>
  label?: string
}) {
  if (!active || !payload || payload.length === 0) return null

  const data = payload[0].payload

  return (
    <div className="bg-popover text-popover-foreground border border-border rounded-lg shadow-lg p-2 text-xs">
      <p className="font-medium mb-1">{label}</p>
      <p>
        Award: {data.award != null ? `$${data.award.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '\u2014'}
      </p>
      <p>
        Max: {data.maxAward != null ? `$${data.maxAward.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '\u2014'}
      </p>
      <p className="font-medium mt-1">
        % of Max: {data.percentOfMax.toFixed(1)}%
      </p>
    </div>
  )
}

export function AwardHistoryChart({ history, className }: AwardHistoryChartProps) {
  // Per CONTEXT.md: "When a clinician has no historical award data, hide the chart section entirely"
  if (history.length === 0) return null

  // Reverse for chronological left-to-right (backend returns most recent first)
  const data: ChartDataPoint[] = [...history].reverse().map((item) => ({
    label: `Q${item.quarter} ${item.year}`,
    percentOfMax:
      item.maxEligibleAward && item.calculatedAward != null
        ? Math.round((item.calculatedAward / item.maxEligibleAward) * 1000) / 10
        : 0,
    award: item.calculatedAward,
    maxAward: item.maxEligibleAward,
  }))

  return (
    <Card className={cn('p-4', className)}>
      <h4 className="text-sm font-medium text-muted-foreground mb-2">
        Award History
      </h4>
      <ResponsiveContainer width="100%" height={130}>
        <LineChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            tickFormatter={(v: number) => `${v}%`}
            width={40}
            axisLine={false}
            tickLine={false}
          />
          <Line
            type="monotone"
            dataKey="percentOfMax"
            stroke="#6366f1"
            strokeWidth={2}
            dot={{ r: 4, fill: '#6366f1', strokeWidth: 0 }}
            activeDot={{ r: 6, fill: '#6366f1' }}
            connectNulls
          />
          <Tooltip content={<AwardTooltip />} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  )
}
